const operationEntityNameCollator = new Intl.Collator('zh-CN', {
  numeric: true,
  sensitivity: 'base',
});

export const getOperationAnalysisSubjectsQueryKey = (organizationId, identityKey) => [
  'operation-analysis-subjects',
  organizationId,
  ...(identityKey === undefined ? [] : [identityKey]),
];

export const invalidateOperationAnalysisSubjects = (queryClient, organizationId) =>
  queryClient.invalidateQueries({
    queryKey: getOperationAnalysisSubjectsQueryKey(organizationId),
  });

const ANALYSIS_QUERY_KEYS = new Set([
  'operation-analysis-subjects',
  'store-directory',
  'store-directory-summary',
  'store-profit-report',
  'operation-analysis-dashboard',
]);

export const invalidateOperationAnalysis = (queryClient, organizationId) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      (ANALYSIS_QUERY_KEYS.has(queryKey[0]) && queryKey[1] === organizationId) ||
      (queryKey[0] === 'store-profit-report-calculation-node' &&
        queryKey[2]?.organizationId === organizationId),
  });

const compareOperationEntities = (left, right) =>
  Number(Boolean(left.storeId)) - Number(Boolean(right.storeId)) ||
  operationEntityNameCollator.compare(left.name, right.name) ||
  String(left.id).localeCompare(String(right.id));

const toUniqueEntityIds = (entityIds) => [
  ...new Set(
    (Array.isArray(entityIds) ? entityIds : [entityIds]).filter(
      (entityId) => typeof entityId === 'string' && entityId.length,
    ),
  ),
];

export const buildOperationEntityTree = (entities = []) => {
  const entityIds = new Set(entities.map(({ id }) => id));
  const childrenByParentId = entities.reduce((result, entity) => {
    const parentId = entity.parentId && entityIds.has(entity.parentId) ? entity.parentId : null;
    const children = result.get(parentId) || [];
    children.push(entity);
    result.set(parentId, children);
    return result;
  }, new Map());
  const buildChildren = (parentId) =>
    [...(childrenByParentId.get(parentId) || [])].sort(compareOperationEntities).map((entity) => {
      const children = buildChildren(entity.id);
      return {
        ...entity,
        key: entity.id,
        title: entity.name,
        value: entity.id,
        ...(children.length ? { children } : {}),
      };
    });

  return buildChildren(null);
};

export const parseOperationAnalysisScope = (query = {}) => {
  if (query.scope !== 'entities') {
    return { type: 'all' };
  }
  const entityIds = toUniqueEntityIds(query.entityId);
  return entityIds.length ? { type: 'entities', entityIds } : { type: 'all' };
};

const getOperationEntityTreeIndex = (treeData) => {
  const parentById = new Map();
  const accessibleIds = new Set();
  const visit = (nodes, parentId = null) => {
    for (const node of nodes) {
      accessibleIds.add(node.value);
      parentById.set(node.value, parentId);
      visit(node.children || [], node.value);
    }
  };
  visit(treeData);
  return { accessibleIds, parentById };
};

export const compressOperationEntityScopeIds = (entityIds, treeData = []) => {
  const selectedIds = toUniqueEntityIds(entityIds);
  const selectedIdSet = new Set(selectedIds);
  const { accessibleIds, parentById } = getOperationEntityTreeIndex(treeData);

  return selectedIds.filter((entityId) => {
    if (!accessibleIds.has(entityId)) {
      return false;
    }
    let ancestorId = parentById.get(entityId);
    while (ancestorId) {
      if (selectedIdSet.has(ancestorId)) {
        return false;
      }
      ancestorId = parentById.get(ancestorId);
    }
    return true;
  });
};

export const normalizeOperationAnalysisScope = (scope, treeData = []) => {
  if (scope?.type !== 'entities') {
    return { type: 'all' };
  }
  const entityIds = compressOperationEntityScopeIds(scope.entityIds, treeData);
  if (!entityIds.length) {
    return { type: 'all' };
  }
  const selectedIds = new Set(entityIds);
  if (treeData.length && treeData.every(({ value }) => selectedIds.has(value))) {
    return { type: 'all' };
  }
  return { type: 'entities', entityIds };
};

export const getOperationAnalysisScopeQueryPatch = (scope) =>
  scope?.type === 'entities' && scope.entityIds?.length
    ? { entityId: toUniqueEntityIds(scope.entityIds), scope: 'entities' }
    : { entityId: undefined, scope: 'all' };

export const resolveOperationAnalysisScopeQuery = (query = {}, treeData = []) => {
  const requestedScope = parseOperationAnalysisScope(query);
  // 旧链接的显式范围全部失效时保留原选择，不能扩大为全组织。
  if (
    requestedScope.type === 'entities' &&
    !compressOperationEntityScopeIds(requestedScope.entityIds, treeData).length
  ) {
    return { queryPatch: {}, scope: requestedScope };
  }
  const scope = normalizeOperationAnalysisScope(requestedScope, treeData);
  const canonicalQuery = getOperationAnalysisScopeQueryPatch(scope);
  let currentEntityIds = [];
  if (typeof query.entityId === 'string') {
    currentEntityIds = [query.entityId];
  } else if (Array.isArray(query.entityId)) {
    currentEntityIds = query.entityId;
  }
  const canonicalEntityIds = canonicalQuery.entityId || [];
  const isCanonical =
    query.scope === canonicalQuery.scope &&
    currentEntityIds.length === canonicalEntityIds.length &&
    currentEntityIds.every((entityId, index) => entityId === canonicalEntityIds[index]);

  return {
    queryPatch: isCanonical ? {} : canonicalQuery,
    scope,
  };
};

export const getOperationEntityScopeTreeSelectValue = (scope, treeData = []) => {
  return scope.type === 'all'
    ? treeData.map(({ value }) => value)
    : compressOperationEntityScopeIds(scope.entityIds, treeData);
};

export const hasOperationAnalysisStores = (scope, treeData) => {
  const selectedIds = new Set(scope.entityIds);
  const visit = (nodes, selected) =>
    nodes.some((node) => {
      const included = selected || selectedIds.has(node.value);
      return Boolean(included && node.storeId) || visit(node.children || [], included);
    });
  return visit(treeData, scope.type === 'all');
};
